import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { useAddonsLlmBalance } from '@newfold/huapi-js';

import {
  productTypes,
  setCtbPurchaseQueryData,
  useCtbProductInfo,
} from '~/hooks/useCtbProductInfo';
import { parseFiniteNumber } from '~/utils/number/helpers';
import { mergeDependentQueryStates } from '~/utils/reactQuery/helpers';

import {
  CREDIT_BALANCE_MAX_POLL_COUNT,
  CREDIT_BALANCE_POLL_INTERVAL_MS,
} from '../utils/constants';
import { AiToolingData } from '../utils/types';
import useLlmAddon from './useLlmAddon';

const useAiToolingData = (): AiToolingData => {
  const {
    addonId,
    hasLlmAddon,
    isPending: isAddonPending,
    isLoading: isAddonLoading,
    isSuccess: isAddonSuccess,
    isFetching: isAddonFetching,
    isError: isAddonError,
    refetch: refetchAddon,
  } = useLlmAddon();

  // Fetches already settled when the current poll budget started. `null` until
  // the first result arrives, and reset to `null` to start a fresh budget.
  const pollStartRef = useRef<number | null>(null);
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const queryClient = useQueryClient();
  const aiCreditProductInfo = useCtbProductInfo(productTypes.AI_CREDIT);

  const balanceQuery = useAddonsLlmBalance(addonId ?? '', {
    query: {
      enabled: !!addonId,
      staleTime: 0,
      // Pause while the browser tab is hidden; resume when visible again.
      refetchIntervalInBackground: false,
      // Poll every 3 min, up to 10 times, then leave it to the refresh button.
      // Counting settled fetches (successes + failures) rather than callback
      // evaluations keeps the budget accurate: React Query re-evaluates this on
      // every state change, and a failing endpoint must not poll forever.
      refetchInterval: (query) => {
        const { dataUpdateCount, errorUpdateCount } = query.state;
        const settledCount = dataUpdateCount + errorUpdateCount;

        // Wait for the initial fetch so it isn't charged to the budget.
        if (settledCount === 0) return CREDIT_BALANCE_POLL_INTERVAL_MS;

        pollStartRef.current ??= settledCount;

        const pollCount = settledCount - pollStartRef.current;

        return (
          pollCount < CREDIT_BALANCE_MAX_POLL_COUNT &&
          CREDIT_BALANCE_POLL_INTERVAL_MS
        );
      },
      select: (response) => response?.data,
    },
  });

  const { credit_balance, credit_expires_at } = balanceQuery.data ?? {};

  const creditBalance = parseFiniteNumber(credit_balance);
  const creditExpiryDate = credit_expires_at ?? '';
  const { isSuccess, dataUpdatedAt } = balanceQuery;
  const { purchaseQueryKey } = aiCreditProductInfo;
  const lastSyncedBalanceUpdateRef = useRef(0);

  useEffect(() => {
    if (!isSuccess || !dataUpdatedAt) return;
    if (lastSyncedBalanceUpdateRef.current === dataUpdatedAt) return;

    lastSyncedBalanceUpdateRef.current = dataUpdatedAt;
    setCtbPurchaseQueryData(queryClient, purchaseQueryKey, {
      hasPurchasedRecently: false,
    });
  }, [dataUpdatedAt, isSuccess, queryClient, purchaseQueryKey]);

  const { isLoading, isError } = mergeDependentQueryStates(
    {
      isPending: isAddonPending,
      isLoading: isAddonLoading,
      isSuccess: isAddonSuccess,
      isFetching: isAddonFetching,
      isError: isAddonError,
      isChildEnabled: !!addonId,
    },
    balanceQuery,
  );

  const refetch = async () => {
    // Manual refresh restarts the poll budget.
    pollStartRef.current = null;
    setIsManualRefreshing(true);
    try {
      await Promise.all([
        refetchAddon(),
        addonId ? balanceQuery.refetch() : Promise.resolve(),
      ]);
    } finally {
      setIsManualRefreshing(false);
    }
  };

  return {
    isLoading,
    isError,
    // Spinner only for user-initiated refresh — not for quiet background polls.
    isRefreshing: isManualRefreshing,
    refetch,
    creditBalance,
    creditExpiryDate,
    hasCredits: creditBalance > 0,
    hasLlmAddon,
    addonId,
  };
};

export default useAiToolingData;
